'use client';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { useBottomNavClearance } from '@ps/functions/hooks/useBottomNavReserve';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { useCurrentUserId } from '@ps/ui/contexts/auth';

import type { OrderMode } from '../../orders/types';
import { ChatProvider } from '../contexts/chat/provider';
import { buildChatSessionId } from '../functions/buildSessionId';
import { ChatSessionType } from '../types/ChatSessionType';
import type { ChatUser } from '../types/ChatUser';
import { ChatMessageList } from './ChatMessageList';
import { ChatRoomNavHeader } from './ChatRoomNavHeader';
import { ChatInputBar } from './composer/ChatInputBar';

interface Props {
  sessionType: ChatSessionType;
  targetUserId: string;
  /** Display name + avatar for the target. Comes from the sessions
      list (`chatControllerGetSessions`); the chat-detail itself
      doesn't load a separate profile yet. */
  targetUser: Pick<ChatUser, 'name' | 'avatar'>;
  transient?: boolean;
  backButton?: boolean;
  /**
   * The chat's bio context, or `null` on the non-1:1 DM inbox (`/messaging`,
   * `/messages`). A 1:1 creator chat + a community member chat pass a real one;
   * threaded to shared post cards for their bio URLs / subscribe flow.
   */
  bioContext: BioContext | null;
  /**
   * Which side of the conversation the signed-in sender is on — drives the
   * composer's "Share Order" scope. `seller` on creator surfaces (dashboard
   * inbox, community member chat), `buyer` on fan surfaces (fan page, DM gate).
   */
  senderRole: OrderMode;
  onBack?: () => void;
  /**
   * When provided, the header shows the Private / Group session-type tabs and
   * calls this on switch. Used by the transient visitor "Message Me" window; the
   * dashboard chat detail omits it (its tabs live in the master pane).
   */
  onSessionTypeChange?: (sessionType: ChatSessionType) => void;
  /** Visitor host handler for a subscription-gated send — routed to the subscribe flow. */
  onSubscribeRequired?: (minLevel?: PopStoreExtendedSubscriptionLevel) => void;
}

/**
 * Top-level chat room — mounts the per-room provider chain
 * (ChatProvider → ChatMessageProvider via the provider itself) and
 * lays out the header / message list / input bar in a vertical
 * column. Returns null while auth is still resolving the user id, so
 * the room never mounts without a signed-in user.
 */
export function ChatRoom({
  sessionType,
  targetUserId,
  targetUser,
  transient,
  backButton,
  bioContext,
  senderRole,
  onBack,
  onSessionTypeChange,
  onSubscribeRequired,
}: Props) {
  const currentUserId = useCurrentUserId();
  // BEFORE the `currentUserId` early-return — a hook after it would be
  // conditional and break hook order on the render where auth isn't ready yet.
  const bottomNavClearance = useBottomNavClearance();
  if (!currentUserId) return null;

  // Private and Group share the same peer room id (sorted `a:b`); only the type
  // differs. Key on it so a tab switch cleanly re-enters + re-fetches.
  const sessionId = buildChatSessionId(currentUserId, targetUserId);

  // Only the private 1:1 thread is replyable, so only it gets an input bar.
  const hasInputBar = sessionType === ChatSessionType.Private;

  return (
    <ChatProvider
      key={sessionType}
      sessionType={sessionType}
      roomId={sessionId}
      sessionId={sessionId}
      currentUserId={currentUserId}
      targetUserId={targetUserId}
      targetUser={targetUser}
      transient={transient}
      backButton={backButton}
      bioContext={bioContext}
    >
      <div className="bg-surface-canvas flex h-full min-h-0 flex-1 flex-col">
        <ChatRoomNavHeader
          onBack={onBack}
          onSessionTypeChange={onSessionTypeChange}
        />
        {/* Only ONE of these two is the room's bottom-most element, and only
            that one should reserve the mobile bottom nav. In a Private session
            the input bar sits below the list and owns it; in a read-only Group
            session there is no input bar, so the list itself runs to the
            viewport bottom and the last message lands under the nav. */}
        <ChatMessageList
          bottomInset={hasInputBar ? undefined : bottomNavClearance}
        />
        {/* Group (promotional) sessions are a read-only broadcast — only the
            private 1:1 thread can be replied to (1:1 with upstream). */}
        {hasInputBar ? (
          <ChatInputBar
            senderRole={senderRole}
            onSubscribeRequired={onSubscribeRequired}
            bottomInset={bottomNavClearance}
          />
        ) : null}
      </div>
    </ChatProvider>
  );
}
